import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { AuthContext } from '../../context/auth';
import { getDataFull, postData, putData, deleteData } from '../../services/ajax';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import MaterialTable, { DataType } from '../../components/material-table';
import { blue, green, red } from '@mui/material/colors';

const validationSchema = yup.object({
    codigo: yup.string().trim().required('El código es obligatorio').max(64, 'Máximo 64 caracteres'),
    categoria_descripcion: yup.string().trim().max(500, 'Máximo 500 caracteres'),
    arancel: yup
        .mixed()
        .nullable()
        .test('arancel', 'Ingresá un número mayor o igual a 0', (val) => {
            if (val === '' || val === null || val === undefined) return true;
            const n = typeof val === 'string' ? val.replace(',', '.').trim() : val;
            const num = Number(n);
            return !isNaN(num) && num >= 0;
        }),
});

function normalizarArancel(val) {
    if (val === '' || val === null || val === undefined) return null;
    const n = typeof val === 'string' ? val.replace(',', '.').trim() : val;
    const num = Number(n);
    if (isNaN(num)) return null;
    return Math.round(num * 100) / 100;
}

function CodigoPage() {
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const alertModal = useContext(AlertModalContext);
    const loadingModal = useContext(LoadingModalContext);

    const puedeGestionar = usuario?.nivel_usuario === 1 || usuario?.sup_usuarios === true;

    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const openNuevo = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEditar = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };

    const cerrarDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const onSubmitForm = async (values, { setSubmitting, resetForm }) => {
        loadingModal.setOpen(true);
        const payload = {
            codigo: values.codigo.trim(),
            categoria_descripcion: values.categoria_descripcion?.trim() || null,
            arancel: normalizarArancel(values.arancel),
        };
        let res;
        if (editing) {
            res = await putData(`codigos/${editing.id}`, payload, usuario.token);
        } else {
            res = await postData('codigos', payload, usuario.token);
        }
        loadingModal.setOpen(false);
        setSubmitting(false);

        if (res.error) {
            enqueueSnackbar(res.error, { variant: 'error' });
            return;
        }
        enqueueSnackbar(editing ? 'Cambios guardados' : 'Código agregado', { variant: 'success' });
        resetForm();
        cerrarDialog();
        setTableMainNeedUpdate(Date.now());
    };

    const onEliminar = (row) => {
        alertModal.showModal(
            '¿Eliminar este código?',
            `Se borrará el código "${row.codigo}". Solo podés eliminarlo si no está usado en ninguna determinación.`,
            async () => {
                loadingModal.setOpen(true);
                const res = await deleteData(`codigos/${row.id}`, usuario.token);
                loadingModal.setOpen(false);
                if (res.error) enqueueSnackbar(res.error, { variant: 'error' });
                else {
                    enqueueSnackbar('Código eliminado', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                }
            }
        );
    };

    const onEditarFromTable = useCallback((row) => {
        openEditar(row);
    }, []);

    const onEliminarFromTable = useCallback(
        (row) => {
            onEliminar(row);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token]
    );

    const tableMain = useMemo(
        () => ({
            title: 'Listado de códigos',
            idName: 'id',
            defaultOrder: 'asc',
            headers: [
                { id: 'id', label: 'Nº', dataType: DataType.NUMBER, sortable: true },
                { id: 'codigo', label: 'Código', dataType: DataType.TEXT, sortable: true },
                { id: 'categoria_descripcion', label: 'Categoría / descripción', dataType: DataType.TEXT, sortable: true },
                { id: 'arancel', label: 'Arancel', dataType: DataType.MONEY, sortable: true },
            ],
            loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
                setRowsTableMain(null);
                const apiPage = page + 1;
                const params = new URLSearchParams();
                params.append('per_page', rowsPerPage.toString());
                params.append('page', apiPage.toString());
                if (orderBy) params.append('order_by', orderBy);
                if (order) params.append('order', order);
                if (searchText) params.append('search_text', searchText);

                const response = await getDataFull(`codigos?${params.toString()}`, usuario.token);

                if (response.error === '') {
                    const list = (response.data || []).map((row) => ({
                        ...row,
                        arancel: row.arancel != null ? Number(row.arancel) : null,
                    }));
                    const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                    pageUpdate(tablePage);
                    totalUpdate(response.meta?.total || 0);
                    filterListUpdate([]);
                    setRowsTableMain(list);
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                    setRowsTableMain([]);
                }
            },
            moreMenuItems: puedeGestionar
                ? [
                      {
                          label: 'Agregar código',
                          secondary: 'Alta de un código de práctica con su arancel',
                          icon: <AddRoundedIcon sx={{ color: green[600] }} />,
                          itemColor: green[600],
                          action: () => openNuevo(),
                      },
                  ]
                : [],
            rowActions: puedeGestionar
                ? (row) => [
                      {
                          label: 'Editar',
                          secondary: 'Modificar código, descripción o arancel',
                          icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                          itemColor: blue[600],
                          action: () => onEditarFromTable(row),
                      },
                      {
                          label: 'Eliminar',
                          secondary: 'Solo si no está vinculado a determinaciones',
                          icon: <DeleteOutlineRoundedIcon sx={{ color: red[600] }} />,
                          itemColor: red[600],
                          dividerBefore: true,
                          action: () => onEliminarFromTable(row),
                      },
                  ]
                : () => [],
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token, puedeGestionar, enqueueSnackbar]
    );

    const heroGradient = useMemo(
        () =>
            theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha('#1a237e', 0.5)} 50%, ${alpha(theme.palette.secondary.main, 0.25)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#e8f4fc', 1)} 45%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
        [theme]
    );

    const initialForm = useMemo(
        () => ({
            codigo: editing?.codigo ?? '',
            categoria_descripcion: editing?.categoria_descripcion ?? '',
            arancel: editing != null && editing.arancel != null && editing.arancel !== '' ? String(editing.arancel) : '',
        }),
        [editing]
    );

    return (
        <Layout nombrePagina="Códigos de práctica">
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Fade in timeout={500}>
                        <Box
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 3,
                                p: { xs: 2, md: 3 },
                                background: heroGradient,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        color: 'primary.main',
                                    }}
                                >
                                    <TagRoundedIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Catálogo de códigos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Cada fila es un <strong>código de práctica</strong> (nomenclador) con su texto de categoría o alcance y el{' '}
                                        <strong>arancel</strong> asociado. Esas piezas se combinan después en cada determinación: una misma determinación puede incluir varios códigos y repetir el mismo código si corresponde; el arancel del ítem es lo que se usa para liquidar ese tramo.
                                    </Typography>
                                </Box>
                                {puedeGestionar && (
                                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openNuevo} sx={{ borderRadius: 2, flexShrink: 0 }}>
                                        Agregar código
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Fade>
                </Grid>

                <Grid size={12}>
                    <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TipsAndUpdatesRoundedIcon color="info" fontSize="small" />
                                <Typography fontWeight={600}>Cómo usar esta pantalla</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                            <Typography variant="body2" color="text.secondary" component="div">
                                <strong>Buscar:</strong> por número de código o por texto de categoría/descripción. <strong>Ordenar:</strong> clic en los encabezados de columna.{' '}
                                <strong>Arancel:</strong> valor numérico de referencia para ese código (podés dejarlo vacío si aún no aplica).{' '}
                                <strong>Acciones:</strong> menú ⋮ por fila. La cantidad de veces que se usa cada código en una determinación se define cuando armes la determinación, no acá.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid size={12}>
                    <Fade in timeout={400}>
                        <Box>
                            <MaterialTable
                                title={tableMain.title}
                                loadData={tableMain.loadData}
                                headers={tableMain.headers}
                                data={rowsTableMain}
                                moreMenuItems={tableMain.moreMenuItems}
                                selectable={false}
                                rowActions={puedeGestionar ? tableMain.rowActions : null}
                                idName={tableMain.idName}
                                defaultOrder={tableMain.defaultOrder}
                                defaultRowsPerPage={10}
                                needUpdate={tableMainNeedUpdate}
                                dense
                            />
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            <Dialog open={dialogOpen} onClose={cerrarDialog} fullWidth maxWidth="sm" TransitionProps={{ timeout: 400 }}>
                <DialogTitle sx={{ pb: 1 }}>{editing ? 'Editar código' : 'Nuevo código de práctica'}</DialogTitle>
                <Formik initialValues={initialForm} validationSchema={validationSchema} enableReinitialize onSubmit={onSubmitForm}>
                    {({ isSubmitting, values, errors, touched, handleChange, handleBlur }) => (
                        <>
                            <DialogContent>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Completá el identificador del nomenclador (ej. 40.01, 1.03), la descripción tal como figura en la normativa y el arancel en pesos si aplica.
                                </Typography>
                                <Form id="form-codigo">
                                    <Stack spacing={2}>
                                        <TextField
                                            name="codigo"
                                            label="Código"
                                            placeholder="Ej.: 40.01, 38.29…"
                                            fullWidth
                                            variant="outlined"
                                            autoFocus
                                            value={values.codigo}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.codigo && errors.codigo)}
                                            helperText={touched.codigo && errors.codigo ? errors.codigo : undefined}
                                        />
                                        <TextField
                                            name="categoria_descripcion"
                                            label="Categoría / descripción"
                                            placeholder="Ej.: Categoría I, COOMBS I…"
                                            fullWidth
                                            variant="outlined"
                                            multiline
                                            minRows={2}
                                            value={values.categoria_descripcion}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.categoria_descripcion && errors.categoria_descripcion)}
                                            helperText={
                                                touched.categoria_descripcion && errors.categoria_descripcion ? errors.categoria_descripcion : undefined
                                            }
                                        />
                                        <TextField
                                            name="arancel"
                                            label="Arancel"
                                            placeholder="Opcional — ej.: 1811 o 37911"
                                            fullWidth
                                            variant="outlined"
                                            value={values.arancel}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.arancel && errors.arancel)}
                                            helperText={
                                                touched.arancel && errors.arancel
                                                    ? errors.arancel
                                                    : 'Podés usar punto o coma decimal. Dejá vacío si no aplica.'
                                            }
                                        />
                                    </Stack>
                                </Form>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <Button onClick={cerrarDialog} color="inherit">
                                    Cancelar
                                </Button>
                                <Button variant="contained" disabled={isSubmitting} size="large" type="submit" form="form-codigo">
                                    {editing ? 'Guardar cambios' : 'Crear código'}
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Formik>
            </Dialog>
        </Layout>
    );
}

export default CodigoPage;
